import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { MapPin, Navigation } from 'lucide-react';

const RouteVisualizationMap = ({ 
  pickup, 
  dropoff, 
  stops = [],
  height = "300px",
  showCard = true 
}) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [mapError, setMapError] = useState(null);
  const [routeInfo, setRouteInfo] = useState(null);

  useEffect(() => {
    if (!pickup || !dropoff) return;
    
    // Wait for Google Maps to load
    const initMap = () => {
      if (!window.google || !window.google.maps) {
        setMapError('Google Maps not loaded');
        return;
      }

      try {
        // Create map centered on a default location (will be adjusted)
        const map = new window.google.maps.Map(mapRef.current, {
          zoom: 12,
          center: { lat: 40.7128, lng: -74.0060 }, // Default NYC
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
        });
        mapInstanceRef.current = map;

        // Build waypoints array from stops
        const allLocations = [pickup, ...stops.map(s => s.address || s), dropoff].filter(Boolean);
        
        if (allLocations.length < 2) {
          setMapError('Need at least pickup and dropoff locations');
          return;
        }

        // Use Directions Service to draw the route
        const directionsService = new window.google.maps.DirectionsService();
        const directionsRenderer = new window.google.maps.DirectionsRenderer({
          map: map,
          suppressMarkers: true, // We'll add custom markers
          polylineOptions: {
            strokeColor: '#C9A46E',
            strokeOpacity: 0.8,
            strokeWeight: 5
          }
        });

        // Build waypoints for intermediate stops
        const waypoints = stops
          .map(s => s.address || s)
          .filter(Boolean)
          .map(address => ({
            location: address,
            stopover: true
          }));

        const request = {
          origin: pickup,
          destination: dropoff,
          waypoints: waypoints,
          travelMode: window.google.maps.TravelMode.DRIVING,
          optimizeWaypoints: false // Keep original order
        };

        directionsService.route(request, (result, status) => {
          if (status === 'OK') {
            directionsRenderer.setDirections(result);
            
            // Add custom markers
            const route = result.routes[0];
            const legs = route.legs;
            
            // Calculate total distance and duration
            let totalDistance = 0;
            let totalDuration = 0;
            legs.forEach(leg => {
              totalDistance += leg.distance.value;
              totalDuration += leg.duration.value;
            });

            setRouteInfo({
              distance: (totalDistance / 1609.34).toFixed(1), // Convert meters to miles
              duration: Math.round(totalDuration / 60), // Convert seconds to minutes
              stops: legs.length - 1
            });

            // Add markers for each location
            // Pickup marker (green)
            new window.google.maps.Marker({
              position: legs[0].start_location,
              map: map,
              icon: {
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: 12,
                fillColor: '#22C55E',
                fillOpacity: 1,
                strokeColor: '#ffffff',
                strokeWeight: 3
              },
              label: {
                text: 'A',
                color: 'white',
                fontWeight: 'bold'
              },
              title: 'Pickup: ' + pickup
            });

            // Intermediate stop markers (blue)
            legs.forEach((leg, index) => {
              if (index < legs.length - 1) {
                new window.google.maps.Marker({
                  position: leg.end_location,
                  map: map,
                  icon: {
                    path: window.google.maps.SymbolPath.CIRCLE,
                    scale: 10,
                    fillColor: '#C9A46E',
                    fillOpacity: 1,
                    strokeColor: '#ffffff',
                    strokeWeight: 2
                  },
                  label: {
                    text: String(index + 1),
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '11px'
                  },
                  title: `Stop ${index + 1}`
                });
              }
            });

            // Dropoff marker (red)
            new window.google.maps.Marker({
              position: legs[legs.length - 1].end_location,
              map: map,
              icon: {
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: 12,
                fillColor: '#EF4444',
                fillOpacity: 1,
                strokeColor: '#ffffff',
                strokeWeight: 3
              },
              label: {
                text: 'B',
                color: 'white',
                fontWeight: 'bold'
              },
              title: 'Dropoff: ' + dropoff
            });

          } else {
            console.error('Directions request failed:', status);
            setMapError('Could not calculate route');
            
            // Fallback: Just show markers without route
            addMarkersWithoutRoute(map, allLocations);
          }
        });

      } catch (error) {
        console.error('Map initialization error:', error);
        setMapError('Failed to initialize map');
      }
    };

    // Fallback function to show markers without route
    const addMarkersWithoutRoute = async (map, locations) => {
      const geocoder = new window.google.maps.Geocoder();
      const bounds = new window.google.maps.LatLngBounds();

      for (let i = 0; i < locations.length; i++) {
        try {
          const result = await new Promise((resolve, reject) => {
            geocoder.geocode({ address: locations[i] }, (results, status) => {
              if (status === 'OK') resolve(results[0]);
              else reject(status);
            });
          });

          const position = result.geometry.location;
          bounds.extend(position);

          let color, label;
          if (i === 0) {
            color = '#22C55E'; // Green for pickup
            label = 'A';
          } else if (i === locations.length - 1) {
            color = '#EF4444'; // Red for dropoff
            label = 'B';
          } else {
            color = '#C9A46E'; // Blue for stops
            label = String(i);
          }

          new window.google.maps.Marker({
            position: position,
            map: map,
            icon: {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: i === 0 || i === locations.length - 1 ? 12 : 10,
              fillColor: color,
              fillOpacity: 1,
              strokeColor: '#ffffff',
              strokeWeight: 2
            },
            label: {
              text: label,
              color: 'white',
              fontWeight: 'bold'
            },
            title: locations[i]
          });
        } catch (error) {
          console.error('Geocoding error for:', locations[i]);
        }
      }

      map.fitBounds(bounds);
    };

    // Check if Google Maps is loaded
    if (window.google && window.google.maps) {
      initMap();
    } else {
      // Wait for it to load
      const checkInterval = setInterval(() => {
        if (window.google && window.google.maps) {
          clearInterval(checkInterval);
          initMap();
        }
      }, 500);

      // Timeout after 10 seconds
      setTimeout(() => {
        clearInterval(checkInterval);
        if (!window.google || !window.google.maps) {
          setMapError('Google Maps failed to load');
        }
      }, 10000);
    }

    return () => {
      if (mapInstanceRef.current) {
        // Cleanup if needed
      }
    };
  }, [pickup, dropoff, stops]);

  const mapContent = (
    <>
      {mapError ? (
        <div className="flex items-center justify-center bg-gray-100 rounded-lg" style={{ height }}>
          <div className="text-center text-gray-500">
            <MapPin className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>{mapError}</p>
          </div>
        </div>
      ) : (
        <div className="relative">
          <div 
            ref={mapRef} 
            style={{ height, width: '100%' }} 
            className="rounded-lg"
          />
          
          {/* Route Info Overlay */}
          {routeInfo && (
            <div className="absolute bottom-3 left-3 bg-white/95 backdrop-blur-sm rounded-lg px-3 py-2 shadow-lg">
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-1">
                  <Navigation className="w-4 h-4 text-blue-600" />
                  <span className="font-medium">{routeInfo.distance} mi</span>
                </div>
                <div className="text-gray-500">
                  ~{routeInfo.duration} min
                </div>
                {routeInfo.stops > 0 && (
                  <div className="text-blue-600">
                    {routeInfo.stops} stop{routeInfo.stops > 1 ? 's' : ''}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Legend */}
          <div className="absolute top-3 right-3 bg-white/95 backdrop-blur-sm rounded-lg px-3 py-2 shadow-lg">
            <div className="flex items-center gap-3 text-xs">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span>Pickup</span>
              </div>
              {stops.length > 0 && (
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  <span>Stops</span>
                </div>
              )}
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <span>Dropoff</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );

  if (!showCard) {
    return mapContent;
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Navigation className="w-5 h-5 text-blue-600" />
          Route Map
        </CardTitle>
      </CardHeader>
      <CardContent>
        {mapContent}
        
        {/* Route Details */}
        <div className="mt-4 space-y-2">
          <div className="flex items-start gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500 mt-1.5 flex-shrink-0"></div>
            <div className="text-sm">
              <span className="font-medium">Pickup:</span> {pickup}
            </div>
          </div>
          
          {stops.map((stop, index) => (
            <div key={index} className="flex items-start gap-2 pl-1">
              <div className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 flex-shrink-0"></div>
              <div className="text-sm text-gray-600">
                <span className="font-medium">Stop {index + 1}:</span> {stop.address || stop}
              </div>
            </div>
          ))}
          
          <div className="flex items-start gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500 mt-1.5 flex-shrink-0"></div>
            <div className="text-sm">
              <span className="font-medium">Dropoff:</span> {dropoff}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default RouteVisualizationMap;
